/**
 * Room Host Tool
 *
 * Hosts the shared `LineTool` subtool (with a `RoomKindAdapter`) for the
 * floorplan "magic room" workflow, exposing automatic and manual drawing via
 * the subtool's mode toggle — replacing the former separate `line_detection`
 * (auto) and `manual_room_draw` tools.
 *
 * Built on the `LineHosting` mixin over `FloorplanToolBase`, so it keeps the
 * floor isolation it inherits (including the custom-segment-isolation slice
 * derived from the subtool's settings) while the mixin provides subtool input
 * routing.
 */

import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { FloorIsolationStrategy, IsolationSpec, defaultFloorIsolationStrategy, xrayException } from '../../../utils/FloorIsolation';
import { SubToolHosting } from '../../SubToolHosting';
import { LineTool } from './LineTool';
import { RoomKindAdapter } from './line-tool/RoomKindAdapter';

/**
 * Scale every room's vertex spheres (1 = full size for the room tools,
 * 0.5 = shrunken elsewhere) and remember the value on the scene so sphere
 * rebuilds outside the tool re-apply the same size. Shared by the magic
 * (this file) and manual room hosts.
 */
export function setRoomSphereScale(viewer: Viewer, scale: number): void {
  viewer.scene.roomSphereScale = scale;
  for (const room of viewer.scene.rooms) {
    for (const sphere of room.spheres) sphere.setScaleFactor(scale);
  }
}

export class RoomHostTool extends SubToolHosting(FloorplanToolBase) {
  readonly name = 'line_detection';

  /** Typed reference to the hosted LineTool for LineTool-specific calls. */
  private readonly lineTool: LineTool;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    const adapter = new RoomKindAdapter();
    this.lineTool = new LineTool(viewer, persona, undefined, adapter);
    const ctx = adapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(this.lineTool, adapter, ctx);

    // A custom-segment-isolation setting changed on the subtool — re-push the
    // clip strategy (this host owns floor isolation) and re-feed the plane.
    this.lineTool.setIsolationSettingsChangedHandler(() => {
      if (this.currentFloorFloorPlane) {
        this.applyFloorIsolation(this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
      }
      this.pushBasePlane();
    });
  }

  // Lifecycle is otherwise owned by SubToolHosting; these overrides only add
  // the vertex-sphere sizing (full size in the tool, half size outside).

  protected onEnable(): void {
    super.onEnable();
    setRoomSphereScale(this.viewer, 1);
  }

  protected onDisable(): void {
    setRoomSphereScale(this.viewer, 0.5);
    super.onDisable();
  }

  protected onFloorChanged(): void {
    this.pushBasePlane();
  }

  /**
   * Per-view spec. The base default already gives 3D fixed-height + X-Ray
   * full-floor; this only overrides the X-Ray exception with the user's
   * custom-segment slice (subtool height/tolerance) when enabled.
   */
  protected getIsolationSpec(): IsolationSpec {
    const floorGroupId = this.currentFloorGroupIdentifier;
    const floor = this.currentFloorFloorPlane;
    const settings = this.lineTool.getCustomIsolationSettings();
    if (!settings.enableCustomSegmentIsolation || !floorGroupId || !floor) {
      return super.getIsolationSpec();
    }

    const tolerance = settings.segmentIsolationTolerance / 2;
    const centerZ = floor.height + settings.segmentIsolationHeight / 2;
    const slice: FloorIsolationStrategy = { kind: 'roomBounds', floorGroupId, floorHeight: centerZ - tolerance, ceilingHeight: centerZ + tolerance };
    return { base: defaultFloorIsolationStrategy(floorGroupId), overrides: xrayException(slice) };
  }

  /** Expose the underlying LineTool subtool (used by DoorWindowTool hosting). */
  getLineSubTool(): LineTool {
    return this.lineTool;
  }
}
